// Email-routing data layer (#325). Reads `GET /v1/email-routes` (the resolved
// default+override matrix) and drives set/reset. The route renders whatever
// comes back — mirrors `lib/keys.ts`: round-trip over `apiFetch` reusing the
// dev session bearer.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { canOwnerWrite } from './roles';
import { SESSION_TOKEN } from './session';

/** One resolved category row — shape the API's `createEmailRouteRoutes` returns. */
export type EmailRouteResolved = {
  readonly category: string;
  readonly destination: string | null;
  readonly is_override: boolean;
};

/**
 * The resolved matrix on the canonical `{data, next_cursor}` envelope, with the
 * account default riding beside it. `next_cursor` is always null: the categories
 * are a fixed vocabulary the route resolves in full, so there is no second page
 * to reach and no Load-more to offer.
 */
export type EmailRoutesResponse = Page<EmailRouteResolved> & {
  readonly default_email: string | null;
};

export const EMAIL_ROUTES_QUERY_KEY = 'email_routes';

/** The resolved routing matrix. Disabled (idle) with no session, like `useMe`. */
export function useEmailRoutes(token: string | null = SESSION_TOKEN) {
  return createQuery<EmailRoutesResponse>(() => ({
    queryKey: [EMAIL_ROUTES_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => apiFetch<EmailRoutesResponse>('/v1/email-routes', token),
  }));
}

/** Set a category override to `address`. Takes effect immediately (verified). */
export async function setEmailRoute(
  category: string,
  address: string,
  token: string | null = SESSION_TOKEN,
): Promise<EmailRouteResolved> {
  return apiFetch<EmailRouteResolved>(`/v1/email-routes/${encodeURIComponent(category)}`, token, {
    method: 'PUT',
    body: JSON.stringify({ address }),
  });
}

/** Drop a category override, resetting it to the account default. Idempotent. */
export async function resetEmailRoute(
  category: string,
  token: string | null = SESSION_TOKEN,
): Promise<EmailRouteResolved> {
  return apiFetch<EmailRouteResolved>(`/v1/email-routes/${encodeURIComponent(category)}`, token, {
    method: 'DELETE',
  });
}

/**
 * Whether to show this caller the account-wide email block on Notifications
 * ("For the whole team"). Same predicate as `canOwnerWrite`, deliberately its
 * own NAME, the `canAccessSecrets` precedent (`lib/roles.ts`): email routes are
 * an owner-only surface, so a member is shown their own address and nothing
 * they cannot change — the component that would read `/v1/email-routes` never
 * mounts for them. An absent role (a PAT) keeps the block; the server is still
 * the arbiter.
 */
export const canManageTeamEmail = canOwnerWrite;
